import React from "react";
import { Commend3StrategyMode } from "../Types";
import {
  DYNAMIC_CATEGORY_HELPER,
  DYNAMIC_CATEGORY_MODE,
  DYNAMIC_CATEGORY_SUFFIX,
  DYNAMIC_TOKEN_HINT,
  READ_ONLY_TITLE_HINT,
} from "../Commend3Constants";

type ModeTableProps = {
  id: string;
  chip: string;
  label: string;
  note: string;
  inScope: boolean;
  modes: Commend3StrategyMode[];
  locked: boolean;
  onWidgetTitleChange: (modeUid: string, value: string) => void;
};

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="tw-h-3 tw-w-3"
    style={{
      stroke: "currentColor",
      fill: "none",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      opacity: 0.65,
    }}
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const HEAD_CELL = "tw-text-left tw-text-[13.5px] tw-font-semibold tw-text-[#0B0C23]";

const ModeTable = ({
  id,
  chip,
  label,
  note,
  inScope,
  modes,
  locked,
  onWidgetTitleChange,
}: ModeTableProps) => {
  const inputStyle: React.CSSProperties = {
    height: "40px",
    padding: "0 12px",
    border: "1px solid #E3E5EC",
    borderRadius: "7px",
    fontSize: "14px",
    color: locked ? "#59596E" : "#0B0C23",
    background: locked ? "#F4F5F9" : "#fff",
    cursor: locked ? "not-allowed" : "text",
  };

  return (
    <div id={id} className="tw-flex tw-flex-col tw-gap-[9px]">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-[13px]">
        <span
          className="tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-px-4 tw-py-1.5 tw-text-[14px] tw-font-semibold"
          style={
            inScope
              ? { background: "#030562", color: "#FFFFFF", border: "1px solid #030562" }
              : { background: "#F4F5F9", color: "#59596E", border: "1px solid #E3E5EC" }
          }
        >
          {chip}
        </span>
        <span className="tw-text-[16px] tw-font-semibold tw-text-[#0B0C23]">{label}</span>
      </div>

      <div className="tw-text-[14px] tw-text-[#59596E]" style={{ lineHeight: 1.55 }}>
        {note}
      </div>

      <div
        className="tw-overflow-hidden tw-rounded-lg"
        style={{ border: "1px solid #E3E5EC" }}
      >
        <table className="tw-w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F4F5F9" }}>
              <th
                className={`${HEAD_CELL} tw-px-4 tw-py-[13px]`}
                style={{ width: "46%", borderBottom: "1px solid #E3E5EC" }}
              >
                Mode
              </th>
              <th
                className={`${HEAD_CELL} tw-px-3 tw-py-[13px]`}
                style={{ width: "14%", borderBottom: "1px solid #E3E5EC" }}
              >
                Type
              </th>
              <th
                className={`${HEAD_CELL} tw-px-4 tw-py-[13px]`}
                style={{ width: "40%", borderBottom: "1px solid #E3E5EC" }}
              >
                Widget title
              </th>
            </tr>
          </thead>
          <tbody>
            {modes.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="tw-px-4 tw-py-6 tw-text-[13.5px] tw-text-[#59596E]"
                >
                  No modes for this strategy.
                </td>
              </tr>
            ) : (
              modes.map((mode) => {
                const primary = mode.type === "Primary";
                const dynamic = mode.name === DYNAMIC_CATEGORY_MODE;

                return (
                  <tr key={mode.uid}>
                    <td
                      className="tw-px-4 tw-py-3.5 tw-align-middle"
                      style={{ borderBottom: "0.5px solid #E3E5EC" }}
                    >
                      <div className="tw-text-[14.5px] tw-font-semibold tw-text-[#0B0C23]">
                        {mode.name}
                      </div>
                      {mode.description ? (
                        <div
                          className="tw-mt-[3px] tw-text-[13px] tw-text-[#59596E]"
                          style={{ lineHeight: 1.5 }}
                        >
                          {mode.description}
                        </div>
                      ) : null}
                    </td>
                    <td
                      className="tw-px-3 tw-py-3.5 tw-align-middle"
                      style={{ borderBottom: "0.5px solid #E3E5EC" }}
                    >
                      <span
                        className="tw-inline-block tw-whitespace-nowrap tw-rounded-full tw-px-3.5 tw-py-[5px] tw-text-[13px]"
                        style={
                          primary
                            ? { background: "#0B5FCB", color: "#FFFFFF", fontWeight: 600 }
                            : {
                                background: "#FFFFFF",
                                color: "#59596E",
                                border: "1px solid #E3E5EC",
                                fontWeight: 500,
                              }
                        }
                      >
                        {primary ? "Primary" : "Fallback"}
                      </span>
                    </td>
                    <td
                      className="tw-px-4 tw-py-3.5 tw-align-middle"
                      style={{ borderBottom: "0.5px solid #E3E5EC" }}
                    >
                      {dynamic ? (
                        <div className="tw-flex tw-flex-col tw-gap-1.5">
                          <div className="tw-flex tw-items-stretch">
                            <input
                              type="text"
                              value={mode.widget_title ?? ""}
                              readOnly={locked}
                              title={locked ? READ_ONLY_TITLE_HINT : undefined}
                              onChange={(event) =>
                                onWidgetTitleChange(mode.uid, event.target.value)
                              }
                              style={{
                                ...inputStyle,
                                flex: 1,
                                minWidth: 0,
                                borderRight: "none",
                                borderRadius: "7px 0 0 7px",
                              }}
                              aria-label={`Widget title for ${mode.name}`}
                            />
                            <span
                              title={DYNAMIC_TOKEN_HINT}
                              className="tw-inline-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-px-[9px] tw-py-1.5 tw-text-[12px]"
                              style={{
                                border: "0.5px solid #E3E5EC",
                                borderRadius: "0 6px 6px 0",
                                background: "#F4F5F9",
                                color: "#B4B9D4",
                                fontStyle: "italic",
                              }}
                            >
                              <LockIcon />
                              {DYNAMIC_CATEGORY_SUFFIX}
                            </span>
                          </div>
                          <div
                            className="tw-text-[12.5px] tw-text-[#59596E]"
                            style={{ lineHeight: 1.5 }}
                          >
                            {DYNAMIC_CATEGORY_HELPER}
                          </div>
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={mode.widget_title ?? ""}
                          readOnly={locked}
                          title={locked ? READ_ONLY_TITLE_HINT : undefined}
                          onChange={(event) =>
                            onWidgetTitleChange(mode.uid, event.target.value)
                          }
                          style={{ ...inputStyle, width: "100%" }}
                          aria-label={`Widget title for ${mode.name}`}
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ModeTable;
